import {
  Caption,
  ControlledNumberField,
  FieldsetCard,
  useFormContainerContext,
  type FieldPrerequisiteSpec,
} from '@if/mui'
import { Grid } from '@mui/material'

import { formatCurrencyNoDecimals } from '@if/core/utils'
import TermAndPlanSelect from '@src/containers/CreateWorksheetPage/components/TermAndPlanSelect'
import { PersonalLoanCalculatorConstants } from '@src/types/Constants'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { useTranslation } from 'react-i18next'

const { MIN_AMOUNT, MAX_PAYMENT_AMOUNT } = PersonalLoanCalculatorConstants

const TERM_AND_PLAN_PREREQUISITES = [
  { name: 'amountRequested', prefixKey: 'calculator', labelKey: 'requestedAmountLabel' },
] as const satisfies readonly FieldPrerequisiteSpec<PersonalLoanCalculatorSchemaType, 'calculator'>[]

interface LoanDetailsFormProps {
  disabled?: boolean
  termAndPlanOptions: TermAndPlanOption[]
  isLoadingTermAndPlan: boolean
  /** Category-based ceiling (higher for Medicard merchants) — see CalculatorForm. */
  maxLoanAmount: number
}

export const LoanDetailsForm = ({
  disabled = false,
  termAndPlanOptions,
  isLoadingTermAndPlan,
  maxLoanAmount,
}: LoanDetailsFormProps) => {
  const { t } = useTranslation()
  const {
    methods: { control },
  } = useFormContainerContext<PersonalLoanCalculatorSchemaType>()

  return (
    <FieldsetCard title={t('calculator.loanDetails')}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <ControlledNumberField
            control={control}
            name="amountRequested"
            label={t('calculator.requestedAmountLabel')}
            fullWidth
            disabled={disabled}
            isMoney
          />
          <Caption sx={{ display: 'block', mt: 0.5 }}>
            {t('calculator.requestedAmountHelper', {
              min: formatCurrencyNoDecimals(MIN_AMOUNT),
              max: formatCurrencyNoDecimals(maxLoanAmount),
            })}
          </Caption>
        </Grid>

        <Grid size={12}>
          <TermAndPlanSelect
            options={termAndPlanOptions}
            isLoading={isLoadingTermAndPlan}
            maxPmtAmount={MAX_PAYMENT_AMOUNT}
            prerequisites={TERM_AND_PLAN_PREREQUISITES}
          />
        </Grid>
      </Grid>
    </FieldsetCard>
  )
}
